/** @format */
import { AxiosRequestConfig, CancelToken } from 'axios';
import { TFunction } from 'i18next';
import { omit } from 'lodash';
import { stringify } from 'qs';

import {
    Market,
    MarketCode,
    defaultMarket,
    euMarket,
    europeanMarkets,
} from '@toucan/config/markets';

import { ItemId, Product, ProductConditionValue } from '@toucan/types/product';

import { client } from '@toucan/services/request-client';

import { centToDecimal, formatAsCurrency } from '@toucan/helpers/currency';
import {
    formatPriceModifiers,
    getProductAttributes,
    getProductConditionDescription,
    getProductIncludesList,
} from '@toucan/helpers/products';

import {
    FacetValue,
    QueryResultFields,
} from '@toucan/client/features/search/config/types';
import { toSnakeCase } from '@toucan/client/global/axios/helpers/interceptors/change-case';
import { ProductSummaryV2 } from '@toucan/client/global/product';
import { getProductIdFromSkuQueryParams } from '@toucan/common/services/product/queries';
import {
    transformProductIdFromSkuResponse,
    transformProductSummaryResponse,
} from '@toucan/common/services/product/serializers';

import {
    fetchQuoteBuyProducts,
    fetchQuoteProductConditions,
} from '../../client/features/search/requests';

export type QuoteProductCondition = {
    /**
     * The value of the product condition
     */
    value: ProductConditionValue;

    /**
     * The min price of the product with a specific condition.
     * This value is expected to be in cent, E.g. £100 = 10000
     */
    minPrice?: number;

    /**
     * The max price of the product with a specific condition.
     * This value is expected to be in cent, E.g. £100 = 10000
     */
    maxPrice?: number;

    /**
     * Will be true when there are no products in that condition
     */
    outOfStock?: boolean;

    /**
     * The product condition text
     */
    text: string;
};

type GetQuoteProductConditionsArgs = {
    /**
     * The model id to search against
     */
    modelId: string;

    /**
     * The locale to search in. E.g. en_GB
     */
    locale?: string;

    /**
     * The market code
     */
    market: MarketCode;

    /**
     * An Axios cancel token
     */
    cancelToken?: CancelToken;

    skuBlocklist?: ItemId[];
};

type QuoteBuyProductsFilterQuery = {
    object_type: string;
    model_id: string;
    product_market: MarketCode;
    product_condition?: ProductConditionValue;
    product_sku?: string;
};

/**
 * Gets product conditions for adding a product to buy quote
 *
 * @param modelId {string} The model id to search against
 * @param cancelToken {string} The cancel token
 * @param locale {string} The locale to search in. E.g. en_GB
 * @param market {MarketCode} The market to search in
 */
export async function getQuoteProductConditions(
    t: TFunction,
    {
        modelId,
        cancelToken,
        locale = defaultMarket.iso,
        market = defaultMarket.code,
        skuBlocklist = [],
    }: GetQuoteProductConditionsArgs
): Promise<QuoteProductCondition[]> {
    const conditions = [
        {
            value: ProductConditionValue.LIKE_NEW,
            starRating: '5',
            text: t('Like new'),
        },
        {
            value: ProductConditionValue.EXCELLENT,
            starRating: '4',
            text: t('Excellent'),
        },
        {
            value: ProductConditionValue.GOOD,
            starRating: '3',
            text: t('Good'),
        },
        {
            value: ProductConditionValue.WELL_USED,
            starRating: '2',
            text: t('Well used'),
        },
        {
            value: ProductConditionValue.HEAVILY_USED,
            starRating: '1',
            text: t('Heavily used'),
        },
    ];

    // TODO - update when modelOperation is added to search-service
    // overrides the product/model (sets to EU) market for EU markets only.
    const productMarket = europeanMarkets.includes(market)
        ? euMarket.code
        : market;

    const filterQuery: QuoteBuyProductsFilterQuery = {
        object_type: 'PRODUCT',
        model_id: modelId,
        product_market: productMarket,
    };

    if (skuBlocklist.length) {
        filterQuery.product_sku = `NOT (${skuBlocklist.join(' OR ')})`;
    }

    const queryParams = stringify(
        {
            filter_query: filterQuery,
            rows: 0,
            facet_field: 'product_condition_star_rating.product_price',
        },
        {
            encodeValuesOnly: true,
            arrayFormat: 'repeat',
        }
    );

    const data = await fetchQuoteProductConditions(
        queryParams,
        cancelToken,
        locale
    );

    const results: FacetValue[] = data.facets?.[0]?.values ?? [];

    return conditions.map((condition) => {
        const result = results.find(
            ({ fieldValue }) => fieldValue === condition.starRating
        );
        const mappedConditionData: QuoteProductCondition = omit(
            condition,
            'starRating'
        );

        if (!result || (result && result.count === '0')) {
            mappedConditionData.outOfStock = true;
        } else {
            mappedConditionData.minPrice = parseInt(result.minimum);
            mappedConditionData.maxPrice = parseInt(result.maximum);
        }

        return mappedConditionData;
    });
}

type GetQuoteBuyProductsArgs = {
    /**
     * Translation function
     */
    t: TFunction;

    /**
     * The active market from context
     */
    activeMarket: Market;

    /**
     * Condition of the product to search for
     */
    condition: ProductConditionValue;

    /**
     * The model id to search for products from
     */
    modelId: string;

    /**
     * Max number of results to return
     */
    limit?: number;

    /**
     * Index of the first result to return
     */
    start?: number;

    /**
     * An Axios cancel token
     */
    cancelToken?: CancelToken;

    /**
     * Array of blacklisted SKUs to exclude from search results
     */
    skuBlacklist?: ItemId[];
};

export type GetQuoteBuyProductsResponse = {
    products: Product[];
    totalResults: number;
};

/**
 * Gets product(s) by condition and modelId from the search service
 */
export async function getQuoteBuyProducts({
    t,
    activeMarket,
    condition,
    modelId,
    cancelToken,
    limit = 5,
    start = 0,
    skuBlacklist = [],
}: GetQuoteBuyProductsArgs): Promise<GetQuoteBuyProductsResponse> {
    // TODO - update when modelOperation is added to search-service
    // overrides the product/model (sets to EU) market for EU markets only.
    const productMarket = europeanMarkets.includes(activeMarket.code)
        ? euMarket.code
        : activeMarket.code;

    const filterQuery: QuoteBuyProductsFilterQuery = {
        object_type: 'PRODUCT',
        model_id: modelId,
        product_condition: condition,
        product_market: productMarket,
    };

    if (skuBlacklist.length) {
        filterQuery.product_sku = `NOT (${skuBlacklist.join(' OR ')})`;
    }

    const queryParams = stringify(
        {
            filter_query: filterQuery,
            field_list: [
                'model_id',
                'model_name',
                'model_url_segment',
                'product_images',
                'product_price',
                'product_sku',
                'product_id',
                'product_url_segment',
                'product_shutter_count',
                'product_hour_count',
                'product_battery_charge_count',
                'product_price_modifiers',
                'model_available_new',
            ],
            start,
            rows: limit,
        },
        {
            encodeValuesOnly: true,
            arrayFormat: 'repeat',
        }
    );

    const data = await fetchQuoteBuyProducts(
        queryParams,
        cancelToken,
        activeMarket.iso
    );

    const formatWithCurrency = formatAsCurrency(
        activeMarket.iso,
        activeMarket.currency
    );

    const products = data.results.map((result: QueryResultFields) => {
        const price = formatWithCurrency(
            centToDecimal(parseInt(result.productPrice.values[0], 10)),
            { trailingZeros: false }
        );

        const shutterCount = result?.productShutterCount
            ? parseInt(result?.productShutterCount?.values[0], 10)
            : undefined;

        const hourCount = result?.productHourCount
            ? parseInt(result?.productHourCount?.values[0], 10)
            : undefined;

        const batteryChargeCount = result?.productBatteryChargeCount
            ? parseInt(result?.productBatteryChargeCount.values[0], 10)
            : undefined;

        const modelAvailableNew =
            result?.modelAvailableNew?.values[0] === 'True';

        const productPriceModifiers =
            result?.productPriceModifiers?.values ?? [];

        const description = getProductIncludesList(
            t,
            productPriceModifiers,
            modelAvailableNew,
            condition
        );

        const conditionDescription = getProductConditionDescription({
            t,
            locale: activeMarket.iso,
            shutterCount,
            hourCount,
            batteryChargeCount,
        });

        const attributes = getProductAttributes({
            t,
            shutterCount,
            hourCount,
            batteryChargeCount,
            locale: activeMarket.iso,
            condition,
        });

        const productSlug = t('product', { ns: 'urls' });

        const fullLink = `/${activeMarket.slug}/${productSlug}/${result?.modelUrlSegment?.values[0]}/${result?.productUrlSegment?.values[0]}`;

        const priceModifiers = formatPriceModifiers(productPriceModifiers);

        return {
            price,
            description,
            condition,
            conditionDescription,
            attributes,
            id: parseInt(result?.productId?.values[0]) || 0,
            value: parseInt(result?.productPrice?.values[0]),
            sku: result?.productSku?.values[0],
            name: result?.modelName?.values[0],
            image: result?.productImages?.values[0],
            modelId: parseInt(result?.modelId?.values[0]),
            linkHref: fullLink,
            priceModifiers,
        };
    });

    return {
        products,
        totalResults: data.totalResults,
    };
}

/**
 * Returns a product summary for a product, irrespective of the state of that
 * product, e.g. Reserved or Allocated.
 *
 * @param id The id of the product to get the details for
 * @param market the market that they're in
 */
export async function getProductSummary(
    id: number,
    market: MarketCode = defaultMarket.code
): Promise<ProductSummaryV2> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const { data } = await client.get(
        `/products/display/v2/${id}/summary/`,
        config
    );

    return transformProductSummaryResponse(data);
}

export async function getProductIdFromSku(
    sku: string,
    slug: string,
    market: string,
    locale: string
): Promise<number | undefined> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Language': toSnakeCase(locale),
            'X-Market': market,
        },
    };
    const queryParams = getProductIdFromSkuQueryParams(sku, slug);

    const { data } = await client.get(
        `/public-api/product-service/v1/product/lookup/?${queryParams}`,
        config
    );

    return transformProductIdFromSkuResponse(data);
}
